import { Card, ProgressBar, Text, TextLink, usePlatformTheme } from '@ui/themed';
import { t } from 'i18next';
import { type ReactNode, useRef } from 'react';
import { useResolvedSteps } from '../../../../components/checklist';
import { INTERCOM_TRIGGER } from '../../../../constants/intercom';
import { isShopify, useEnv } from '../../../../context/env-context';
import { useHandlers } from '../../../../context/page-context';
import { useSettings, useUpdateSettings } from '../../../../context/settings-context';
import { useStorageKey } from '../../../../context/use-storage-key';
import { useConfetti } from '../../../../hooks/use-confetti';
import { FinishSetupCard } from '../../../home/components/finish-setup-card';
import type { OnboardingChecklistStep } from '../../shared/types';
import { useSetupSteps } from '../use-setup-steps';

export type SetupBannerProps = {
  /** Set false on pages that should only nudge unfinished setup, not host the launch prompt. */
  showLaunchPrompt?: boolean;
};

/**
 * Pre-launch prompt. A CTA into setup while incomplete; once complete, a launch prompt whose button
 * flips `hasLaunched`. Hides itself post-launch. Steps come from `SetupStepsProvider`.
 */
export function SetupBanner({ showLaunchPrompt = true }: SetupBannerProps) {
  const { connected_platform } = useEnv();
  const { hasLaunched = true } = useSettings();
  const setupSteps = useSetupSteps();
  const theme = usePlatformTheme();

  if (hasLaunched) {
    return null;
  }

  if (isShopify(connected_platform) && theme === 'spark') {
    return <FinishSetupCard />;
  }

  // TODO: open up once we support more platforms
  if (theme !== 'wix') return null;

  // Setup Banner for other platforms
  return <ResolvedBanner setupSteps={setupSteps} showLaunchPrompt={showLaunchPrompt} />;
}

function ResolvedBanner({
  setupSteps,
  showLaunchPrompt,
}: {
  setupSteps: OnboardingChecklistStep[];
  showLaunchPrompt: boolean;
}) {
  const storageKey = useStorageKey('setup');
  const { probes, activeStep, isComplete, completed, total } = useResolvedSteps(
    setupSteps,
    storageKey,
  );

  function banner(): ReactNode {
    // `activeStep` is null until settled, so the counts are accurate by the time this card shows.
    if (activeStep)
      return (
        <ContinueSetupCard completed={completed} total={total} activeStepTitle={activeStep.title} />
      );
    if (isComplete && showLaunchPrompt) return <LaunchCard />;
    return null;
  }

  return (
    <>
      {/* Hidden — these gather each step's status; they drive which banner (if any) shows. */}
      {probes}
      {banner()}
    </>
  );
}

function ContinueSetupCard({
  completed,
  total,
  activeStepTitle,
}: {
  completed: number;
  total: number;
  activeStepTitle: string;
}) {
  const { navigationHandler } = useHandlers();

  return (
    <Card>
      <Card.Header
        headingLevel="h2"
        icon="rocket"
        primaryAction={{
          label: t('features:onboarding.components.setup_banner.cta'),
          icon: 'arrowRight',
          onClick: () => navigationHandler.navigateById('onboarding.setup'),
          variant: 'primary',
        }}
      >
        {t('features:onboarding.components.setup_banner.title')}
      </Card.Header>
      <Card.Body>
        <ProgressBar
          title={{
            text: t('features:onboarding.components.setup_banner.next', {
              title: activeStepTitle,
            }),
            style: 'sm',
          }}
          caption
          size="lg"
          segments={total}
          filled={completed}
          highlightActive
        />
      </Card.Body>
    </Card>
  );
}

function LaunchCard() {
  const { mutate: updateSettings } = useUpdateSettings();
  const { finale } = useConfetti();
  // The launch CTA is the card's first button; anchor the finale to it (Card/Button forward no ref).
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div ref={ref}>
      <Card>
        <Card.Header
          headingLevel="h2"
          icon="checkCircle"
          primaryAction={{
            label: t('features:onboarding.components.setup_banner.launch_cta'),
            icon: 'rocket',
            onClick: () => {
              finale({ anchor: ref.current?.querySelector('button') });
              updateSettings({ key: 'hasLaunched', value: true });
            },
            variant: 'primary',
          }}
        >
          {t('features:onboarding.components.setup_banner.launch_title')}
        </Card.Header>
        <Card.Body>
          <Text variant="secondary">
            {t('features:onboarding.components.setup_banner.launch_body')}{' '}
            {/* Span carries the Intercom trigger class; the click bubbles up from TextLink. */}
            <span className={INTERCOM_TRIGGER.onboardingChat}>
              <TextLink variant="secondary" onClick={() => {}}>
                {t('features:onboarding.components.setup_banner.talk_to_onboarding')}
              </TextLink>
            </span>
          </Text>
          {/* TODO: add a "Place a test order" secondary CTA here (and restore the test-order clause
              in `launch_body`) once the try-it-out / tutorial page is wired to a nav route. */}
        </Card.Body>
      </Card>
    </div>
  );
}
